/**
 * Context Binding Engine (CBE) — phase-1 type vocabulary.
 *
 * The CBE is ONE canonical, assumption-free, declarative `tag → field`
 * engine that consumes already-extracted `ExtractedClaim` facts (reading
 * the mandatory `sourceSpan` source text) and emits typed, provenanced
 * resolutions to auto-fill intent + financial-assumption + SED fields,
 * COMPLEMENTING (never clobbering) BOM + Intent.
 *
 * WIRING STATUS: all four callers are LIVE.
 *   1. Financial pull  — contextFinancialFill.ts / pullFinancialFromSources store action.
 *   2. SED-send        — buildSiteContextBlock (reportPrompts.ts), CBE-A2 markers.
 *   3. Bi-dir sync     — syncSedContextBindings.ts / sedAmendments store path.
 *   4. Intent fill     — contextIntentFill.ts / the store claim-merge paths (gap-fill only).
 * See the CBE design memo (`ingestion_engine_cbe_design.md`, wf_0a558b66-4f4 / w9aw8n2gt).
 *
 * Engineering rules (CLAUDE.md §2, §4):
 *   - A range NEVER collapses to a scalar (no midpoint). The discriminated
 *     `CbeValue` union carries `{min, max, unit}` end-to-end so the
 *     downstream input field can decide whether it is range-capable.
 *   - Unit / currency parsing is the §4 1000×-error risk class. The
 *     `valueKind` discriminator + the `unit` carrier keep the parse
 *     unambiguous ($/MWh ≠ $/kWh, $/MW ≠ $/kW, M ≠ B).
 *   - Provenance is mandatory: every value carries the originating tag +
 *     the verbatim `sourceSpan` so the UI can render a "from {source}" chip
 *     and the no-fabrication guards can verify the value appears in span.
 */
import type { ClaimCategory } from '../../types/siteContext';

/* ── Fact (engine input) ──────────────────────────────────────────────
 *
 * The CBE consumes the {tag, value, sourceText, confidence} projection of
 * an `ExtractedClaim`:
 *   tag        = ExtractedClaim.category
 *   value      = ExtractedClaim.claimedValue (already-parsed scalar)
 *   sourceText = ExtractedClaim.sourceSpan   (the MANDATORY source excerpt)
 *   confidence = ExtractedClaim.extractorConfidence
 *
 * `field` mirrors `ExtractedClaim.field` for sub-tag disambiguation; the
 * full `ExtractedClaim` is the canonical fact, but the engine only reads
 * these four (+field/sedField) so it stays decoupled from claim plumbing.
 */
export interface CbeFact {
  /** The extraction tag — `ExtractedClaim.category`. */
  readonly tag: ClaimCategory;
  /** Already-parsed scalar — `ExtractedClaim.claimedValue`. */
  readonly value: string | number | boolean | undefined;
  /** The MANDATORY verbatim source excerpt — `ExtractedClaim.sourceSpan`. */
  readonly sourceText: string;
  /** Extractor confidence 0-100 — `ExtractedClaim.extractorConfidence`. */
  readonly confidence: number;
  /** Optional sub-field tag — `ExtractedClaim.field`. */
  readonly field?: string;
  /** Optional SED field hint carried on the claim — `ExtractedClaim.sedField`. */
  readonly sedField?: string;
}

/* ── Value qualifiers + provenance ────────────────────────────────────── */

/**
 * Soft qualifier on a coerced value, lifted from the source text. NEVER
 * fabricates precision: `'approximate'` ("~1.5 MW") keeps the scalar and
 * lowers confidence — it does NOT invent a ±band. `'at-least'`/`'at-most'`
 * mark an open-ended range (">200" / "<10").
 */
export type CbeQualifier = 'approximate' | 'at-least' | 'at-most';

/** Precision of a parsed date — drives how the UI renders it. */
export type CbeDatePrecision = 'day' | 'month' | 'quarter' | 'year';

/**
 * Where a value came from. `tag` + `sourceSpan` are mandatory so the UI
 * can render a "from {source}" chip and the no-fabrication guards can
 * verify the coerced value actually appears in the span.
 */
export interface CbeProvenance {
  readonly tag: ClaimCategory;
  readonly sourceSpan: string;
}

/* ── CbeValue discriminated union ─────────────────────────────────────── */

/** Fields every `CbeValue` variant carries. */
interface CbeValueBase {
  /** Soft qualifier lifted from the span, if any. NEVER fabricates precision. */
  readonly qualifier?: CbeQualifier;
  /**
   * Confidence the engine assigns to THIS value (0-100). Seeded from the
   * extractor confidence; a qualifier (e.g. `'approximate'`) lowers it.
   */
  readonly confidence: number;
  /** Originating tag + verbatim source span. Mandatory. */
  readonly provenance: CbeProvenance;
}

/** A single clean scalar — number or string. */
export interface CbeScalarValue extends CbeValueBase {
  readonly kind: 'scalar';
  readonly value: number | string;
  /** Normalised unit token when known (e.g. 'MW', '$/MWh', 'acres'). */
  readonly unit?: string;
}

/**
 * A stated range — NEVER collapsed to a midpoint. `min` / `max` may be
 * `null` for an open range (">200" → `{min:200, max:null}`).
 */
export interface CbeRangeValue extends CbeValueBase {
  readonly kind: 'range';
  readonly min: number | null;
  readonly max: number | null;
  /** Normalised unit token (e.g. 'MW', '$/MW', 'acres', '$'). */
  readonly unit?: string;
}

/** An ISO date with the precision actually stated in the source. */
export interface CbeDateValue extends CbeValueBase {
  readonly kind: 'date';
  /** ISO yyyy-mm-dd. Month/quarter/year precisions normalise to the 1st. */
  readonly iso: string;
  readonly precision: CbeDatePrecision;
}

/** A boolean — polarity read from the span (`detectNegationPolarity`). */
export interface CbeBoolValue extends CbeValueBase {
  readonly kind: 'bool';
  readonly value: boolean;
}

/** An enum member — only emitted when EXPLICIT in the span. */
export interface CbeEnumValue extends CbeValueBase {
  readonly kind: 'enum';
  readonly value: string;
}

/**
 * A complex free-form value that cannot be typed further but is still
 * worth carrying (e.g. a labelled descriptor). Never coerced to a number.
 */
export interface CbeComplexValue extends CbeValueBase {
  readonly kind: 'complex';
  readonly value: string;
}

/**
 * A multi / count×per-unit×total figure ("4 x 150 MVA" → count 4, perUnit
 * 150, total 600). `total = count × perUnit` is computed, NOT assumed when
 * absent.
 */
export interface CbeMultiValue extends CbeValueBase {
  readonly kind: 'multi';
  readonly count: number;
  readonly perUnit: number;
  readonly total: number;
  /** Normalised per-unit unit token (e.g. 'MVA', 'MW'). */
  readonly unit?: string;
}

/** The CBE typed-value discriminated union. */
export type CbeValue =
  | CbeScalarValue
  | CbeRangeValue
  | CbeDateValue
  | CbeBoolValue
  | CbeEnumValue
  | CbeComplexValue
  | CbeMultiValue;

/** The `kind` discriminator of a {@link CbeValue}. */
export type CbeValueKind = CbeValue['kind'];

/* ── Binding declaration ──────────────────────────────────────────────── */

/** Surface a binding writes into. */
export type CbeSurface = 'sed' | 'financial' | 'financialPhase' | 'intent';

/** Target field a binding fills. */
export interface CbeTargetField {
  readonly surface: CbeSurface;
  /** Dot-path into the surface (e.g. 'parcel.developableAcres'). */
  readonly path: string;
}

/** Coercion strategy a binding applies to the fact value + source span. */
export type CbeCoercionKind =
  | 'toNumber'
  | 'toDate'
  | 'toBool'
  | 'toEnum'
  | 'toRange'
  | 'toPercent'
  | 'toYears'
  | 'toString'
  | 'unitNormalize';

/**
 * Source-ownership for a target field.
 *   - `'owns'`        ⇒ Context is authoritative for this field (no other
 *                       source reaches it). Fills + refreshes.
 *   - `'complements'` ⇒ another source (BOM / Intent) owns it; Context only
 *                       GAP-FILLS a blank, never overwrites the owner, and
 *                       a both-present divergence escalates to a conflict.
 */
export type CbeOwnership = 'owns' | 'complements';

/** Fill behaviour for a binding. */
export interface CbeFillPolicy {
  readonly ownership: CbeOwnership;
  /**
   * Precedence rank for ordering when multiple bindings target the same
   * field. Lower rank = higher authority (wins / fills first). NaN-guarded
   * at merge time.
   */
  readonly precedenceRank: number;
  /**
   * Whether a later context run may REFRESH a value this binding previously
   * wrote (vs leave it pinned). Optional; absent ⇒ refreshable.
   */
  readonly refreshable?: boolean;
}

/**
 * A `FactMatcher` decides whether THIS binding row should fire for THIS
 * fact. Runs over the whole fact (tag already matched by the caller); the
 * locus / regex variants read `sourceText` to disambiguate one-tag-many-
 * fields (the 332-vs-800 acres, NTP-vs-COD which-of-many cases).
 */
export type CbeFactMatcher =
  /** tag → single field (always fires once the tag matches). */
  | { readonly kind: 'always' }
  /** Fires when `fact.field === field` (sub-tag disambiguation). */
  | { readonly kind: 'subField'; readonly field: string }
  /**
   * Fires when one of `keywords` occurs in `sourceText` AND the value binds
   * nearest that keyword. Reuses the proven nearest-keyword binder.
   */
  | {
      readonly kind: 'locusKeyword';
      readonly keywords: readonly string[];
      readonly window?: number;
    }
  /** Fires when `re` matches `sourceText`. */
  | { readonly kind: 'spanRegex'; readonly re: RegExp };

/**
 * A single declarative binding row. The registry is `CbeBinding[]`.
 *
 * Authoring contract:
 *   - PURE. No I/O, no external state. `coercion` MAY read `sourceText`.
 *   - One tag may have MANY rows (fan-out); the `factMatcher` disambiguates
 *     which row fires for a given fact.
 *   - `valueKind` MUST match the coercion's output kind — pinned by a test.
 */
export interface CbeBinding {
  /** The extraction tag this row keys on (`ExtractedClaim.category`). */
  readonly sourceTag: ClaimCategory;
  /** Disambiguator for one-tag-many-fields. */
  readonly factMatcher: CbeFactMatcher;
  /** Where the coerced value lands. */
  readonly targetField: CbeTargetField;
  /** How the fact value + span coerce into a typed value. */
  readonly coercion: CbeCoercionKind;
  /** The `CbeValue.kind` the coercion is expected to produce. */
  readonly valueKind: CbeValueKind;
  /** Source-ownership + precedence + refreshability. */
  readonly fillPolicy: CbeFillPolicy;
  /** Human-readable label for the diff-tab / provenance chip. */
  readonly label: string;
}

/* ── Resolution (engine output) ───────────────────────────────────────── */

/**
 * Why a binding matched but produced NO value. Surfaced so the UI can say
 * "no clean value — enter manually" rather than silently dropping a field.
 */
export type CbeSkipReason =
  /** The coercion could not type the value cleanly. */
  | 'unclean-coercion'
  /** The coerced number does not appear near its keyword in the span. */
  | 'value-not-in-span'
  /** A range target with no range-capable widget — scalar left empty. */
  | 'range-no-scalar'
  /** The enum value was not explicitly present in the span. */
  | 'enum-not-explicit'
  /** The fact had no value to coerce. */
  | 'no-value';

/**
 * One resolved (or skipped) binding. The engine emits `CbeResolution[]`;
 * the merge step orders + complements them.
 */
export interface CbeResolution {
  /** The binding that produced this resolution. */
  readonly binding: CbeBinding;
  /** The target field (denormalised from the binding for convenience). */
  readonly targetField: CbeTargetField;
  /**
   * The coerced typed value, or `undefined` when the binding matched but
   * the coercion / guards rejected it (see `skipReason`).
   */
  readonly value: CbeValue | undefined;
  /**
   * Set when `value` is `undefined` AND the binding matched — tells the UI
   * why nothing was filled. Absent when `value` is present.
   */
  readonly skipReason?: CbeSkipReason;
  /**
   * Low-confidence flag (extractor confidence < the floor). The value still
   * fills but is surfaced for confirmation, never a silent high-confidence
   * default.
   */
  readonly lowConfidence: boolean;
}

/** Confidence floor below which a fill is flagged for confirmation. */
export const CBE_LOW_CONFIDENCE_FLOOR = 50;
